import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm } from "../contexts/ConfirmProvider";
import { useOrg } from "../contexts/OrgProvider";
import { motion } from "framer-motion";
import {
  Search, AlertTriangle, CheckCircle2, Clock, XCircle,
  ChevronDown, ChevronUp, FileWarning, Plus, Play, Loader2,
  Building2, User, Mail, Calendar, Trash2, ArrowLeft, MoreVertical
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PageHeader } from "../components/PageHeader";
import { FW_NAMES as fwNames, FW_COLORS as fwColors } from "../constants/frameworks";

const GapAnalysis = () => {
  const { token } = useAuth();
  const { t, lang } = useI18n();
  const confirm = useConfirm();
  const { activeFrameworks } = useOrg();
  const fwEntries = Object.entries(fwNames).filter(([id]) => activeFrameworks.includes(id));
  // Global overview state
  const [summaries, setSummaries] = useState(null);
  const [selectedFw, setSelectedFw] = useState("iso27001");
  const [analysis, setAnalysis] = useState(null);
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGap, setExpandedGap] = useState(null);
  const [editingGap, setEditingGap] = useState(null);
  const [savingGap, setSavingGap] = useState(false);
  const [evidenceList, setEvidenceList] = useState([]);
  const [documentList, setDocumentList] = useState([]);

  // Projects state
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [managedOrgs, setManagedOrgs] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [running, setRunning] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [form, setForm] = useState({
    client_org_id: "", project_name: "", contact_name: "", contact_email: "",
    framework: "iso27001", responsible: "", notes: ""
  });

  const fetchGlobal = useCallback(async () => {
    setLoadingGlobal(true);
    try {
      const res = await axios.get(`${API}/gap-analysis/summary/all`, { headers: { Authorization: `Bearer ${token}` } });
      setSummaries(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingGlobal(false); }
  }, [token]);

  const fetchAnalysis = useCallback(async (fw) => {
    try {
      const res = await axios.get(`${API}/gap-analysis/${fw}`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalysis(res.data);
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const [pRes, oRes] = await Promise.all([
        axios.get(`${API}/engagements?assessment_type=gap`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tenants/managed-orgs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProjects(pRes.data || []);
      setManagedOrgs(oRes.data || []);
    } catch (e) { console.error(e); }
    finally { setLoadingProjects(false); }
  }, [token]);

  // Fetch the evidence + document catalogs once so the edit dialog can offer them as options
  const fetchCatalogs = useCallback(async () => {
    try {
      const [ev, dc] = await Promise.all([
        axios.get(`${API}/evidence`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setEvidenceList(ev.data || []);
      setDocumentList(dc.data || []);
    } catch { /* non-fatal */ }
  }, [token]);

  const saveFinding = async () => {
    if (!editingGap) return;
    setSavingGap(true);
    try {
      const payload = {
        status: editingGap.status,
        priority: editingGap.priority,
        notes: editingGap.notes || null,
        remediation_plan: editingGap.remediation_plan || null,
        owner: editingGap.owner || null,
        due_date: editingGap.due_date || null,
        evidence_refs: editingGap.evidence_refs || [],
        document_refs: editingGap.document_refs || [],
      };
      await axios.put(
        `${API}/gap-analysis/${selectedFw}/finding/${encodeURIComponent(editingGap.control_number)}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("gap.finding_saved"));
      setEditingGap(null);
      await fetchAnalysis(selectedFw);
    } catch (err) {
      toast.error(extractError(err, t("gap.finding_save_failed")));
    } finally {
      setSavingGap(false);
    }
  };

  const resetFinding = async () => {
    if (!editingGap) return;
    if (!(await confirm({ title: t("gap.discard_title"), description: t("gap.discard_confirm"), destructive: true }))) return;
    setSavingGap(true);
    try {
      await axios.delete(
        `${API}/gap-analysis/${selectedFw}/finding/${encodeURIComponent(editingGap.control_number)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("gap.finding_reverted"));
      setEditingGap(null);
      await fetchAnalysis(selectedFw);
    } catch {
      toast.error(t("gap.revert_failed"));
    } finally {
      setSavingGap(false);
    }
  };

  useEffect(() => { fetchGlobal(); fetchProjects(); fetchCatalogs(); }, [fetchGlobal, fetchProjects, fetchCatalogs]);
  useEffect(() => {
    if (activeFrameworks.length > 0 && !activeFrameworks.includes(selectedFw)) {
      setSelectedFw(activeFrameworks[0]);
    }
  }, [activeFrameworks, selectedFw]);
  useEffect(() => { fetchAnalysis(selectedFw); }, [selectedFw, fetchAnalysis]);

  const resetForm = () => setForm({
    client_org_id: managedOrgs[0]?.organization_id || "", project_name: "", contact_name: "",
    contact_email: "", framework: "iso27001", responsible: "", notes: ""
  });

  const openCreate = () => { resetForm(); setShowCreate(true); };

  const submitCreate = async () => {
    if (!form.client_org_id || !form.project_name || !form.framework) {
      toast.error(t("gap.client_project_fw_required"));
      return;
    }
    setCreating(true);
    try {
      const payload = { ...form, assessment_type: "gap" };
      const res = await axios.post(`${API}/engagements`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("gap.project_created_running"));
      setShowCreate(false);
      // Auto-run gap
      await runProjectGap(res.data.engagement_id);
      fetchProjects();
    } catch (e) {
      toast.error(extractError(e, "Failed"));
    } finally { setCreating(false); }
  };

  const runProjectGap = async (engagementId) => {
    setRunning(engagementId);
    try {
      const res = await axios.post(`${API}/engagements/${engagementId}/run-gap`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${t("gap.assessment_completed")}: ${res.data.snapshot.coverage_percentage}% ${t("gap.coverage")}`);
      fetchProjects();
      return res.data;
    } catch (e) {
      toast.error(extractError(e, "Failed to run"));
    } finally { setRunning(""); }
  };

  const markCompleted = async (engagementId) => {
    try {
      await axios.put(`${API}/engagements/${engagementId}/status?status=completed`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("gap.project_marked_completed"));
      fetchProjects();
    } catch { toast.error("Failed"); }
  };

  const deleteProject = async (engagementId, returnFocusTo) => {
    // El disparador real es un <DropdownMenuItem> que Radix desmonta al
    // cerrar el menú "…" — pasamos el botón "…" persistente (returnFocusTo)
    // para que el foco no caiga en un nodo muerto (ver ConfirmProvider.jsx).
    if (!(await confirm({ title: t("gap.delete_title"), description: t("gap.delete_confirm"), destructive: true, returnFocusTo }))) return;
    try {
      await axios.delete(`${API}/engagements/${engagementId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("gap.deleted"));
      fetchProjects();
    } catch { toast.error("Failed"); }
  };

  const filteredGaps = (analysis?.gaps || []).filter(g => {
    if (filterPriority !== "all" && g.priority !== filterPriority) return false;
    if (filterStatus !== "all" && g.status !== filterStatus) return false;
    if (searchQuery && !g.title.toLowerCase().includes(searchQuery.toLowerCase()) && !g.control_number.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const pieData = analysis ? [
    { name: t("implemented"), value: analysis.implemented, color: "#22C55E" },
    { name: t("in_progress"), value: analysis.in_progress, color: "#EAB308" },
    { name: t("not_started"), value: analysis.not_started, color: "#EF4444" },
  ].filter(d => d.value > 0) : [];

  const getScoreColor = (pct) => pct >= 80 ? "text-green-500" : pct >= 60 ? "text-yellow-500" : pct >= 30 ? "text-orange-500" : "text-red-500";

  const statusBadge = (s) => {
    const map = {
      in_progress: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
      completed: "bg-green-500/10 text-green-600 border-green-500/30",
      on_hold: "bg-gray-500/10 text-gray-600 border-gray-500/30",
      cancelled: "bg-red-500/10 text-red-600 border-red-500/30"
    };
    return map[s] || map.in_progress;
  };

  return (
    <div className="p-6 space-y-6" data-testid="gap-analysis-page">
      <PageHeader
        title={t("gap.title")}
        description={t("gap.subtitle")}
        actions={
          <Button onClick={openCreate} data-testid="new-gap-btn">
            <Plus className="w-4 h-4 mr-2" />{t("gap.new_project")}
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="projects" data-testid="tab-projects">{t("gap.my_projects")}</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview">{t("gap.overview")}</TabsTrigger>
        </TabsList>

        {/* --- PROJECTS TAB --- */}
        <TabsContent value="projects" className="space-y-4 mt-4">
          {loadingProjects ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
          ) : selectedProject ? (
            <ProjectDetailView
              project={selectedProject}
              onBack={() => setSelectedProject(null)}
              token={token}
              onRerun={runProjectGap}
              running={running === selectedProject.engagement_id}
              t={t}
              lang={lang}
            />
          ) : projects.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="py-12 text-center space-y-3">
                <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto" />
                <p className="text-muted-foreground">{t("gap.no_projects")}</p>
                <Button onClick={openCreate} data-testid="new-gap-empty-btn">
                  <Plus className="w-4 h-4 mr-2" />{t("gap.new_project")}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map(p => {
                const snap = p.result_snapshot;
                return (
                  <motion.div key={p.engagement_id} whileHover={{ scale: 1.02 }}>
                    <Card className="border-border h-full cursor-pointer" onClick={() => setSelectedProject(p)} data-testid={`project-${p.engagement_id}`}>
                      <CardContent className="pt-6 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold truncate">{p.project_name}</h3>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                              <Building2 className="w-3 h-3" />{p.client_name}
                            </div>
                          </div>
                          <Badge variant="outline" className={statusBadge(p.status)}>
                            {t(`eng.status.${p.status}`)}
                          </Badge>
                        </div>

                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: fwColors[p.framework] }} />
                          <span className="text-xs font-medium">{fwNames[p.framework]}</span>
                        </div>

                        {snap ? (
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">{t("gap.coverage")}</span>
                              <span className={`font-bold ${getScoreColor(snap.coverage_percentage)}`}>{snap.coverage_percentage}%</span>
                            </div>
                            <Progress value={snap.coverage_percentage} className="h-1.5" />
                            <p className="text-[11px] text-muted-foreground">{snap.gap_count} {t("gap.gaps_found")}</p>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">{t("gap.never_run")}</p>
                        )}

                        <div className="space-y-1 text-[11px] text-muted-foreground border-t border-border pt-2">
                          {p.contact_name && <div className="flex items-center gap-1"><User className="w-3 h-3" />{p.contact_name}</div>}
                          {p.responsible && <div className="flex items-center gap-1"><User className="w-3 h-3" />{p.responsible}</div>}
                          {snap?.ran_at && <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />{t("gap.last_run")}: {new Date(snap.ran_at).toLocaleDateString()}</div>}
                        </div>

                        <div className="flex gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="outline" className="flex-1" onClick={() => runProjectGap(p.engagement_id)}
                            disabled={running === p.engagement_id} data-testid={`rerun-${p.engagement_id}`}>
                            {running === p.engagement_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                            {t("gap.run_again")}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={t("more") || "Más"}
                                data-testid={`actions-${p.engagement_id}`}
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => deleteProject(p.engagement_id, `[data-testid="actions-${p.engagement_id}"]`)}
                                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                                data-testid={`delete-${p.engagement_id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-2" />
                                {t("delete")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* --- OVERVIEW TAB --- */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {fwEntries.map(([id, name]) => {
              const s = summaries?.[id];
              const isSelected = selectedFw === id;
              return (
                <motion.div key={id} whileHover={{ scale: 1.02 }}>
                  <Card className={`border-border cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary border-primary' : 'hover:border-primary/50'}`}
                    onClick={() => setSelectedFw(id)} data-testid={`gap-fw-card-${id}`}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: fwColors[id] }} />
                        <span className="text-xs font-medium">{name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-2xl font-heading font-bold ${getScoreColor(s?.coverage_percentage || 0)}`}>
                          {s?.coverage_percentage || 0}%
                        </span>
                        <span className="text-xs text-muted-foreground">{s?.gap_count || 0} {t("gap.gaps_found")}</span>
                      </div>
                      <Progress value={s?.coverage_percentage || 0} className="h-1.5 mt-2" />
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {analysis && !loadingGlobal && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <Card className="border-border">
                  <CardHeader className="pb-2"><CardTitle className="text-lg">{fwNames[selectedFw]} {t("gap.coverage")}</CardTitle></CardHeader>
                  <CardContent>
                    <div className="text-center mb-4">
                      <span className={`text-5xl font-heading font-bold ${getScoreColor(analysis.coverage_percentage)}`}>
                        {analysis.coverage_percentage}%
                      </span>
                    </div>
                    {pieData.length > 0 ? (
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2} dataKey="value">
                              {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">{t("no_data")}</div>}
                    <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                      <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /><span>{analysis.implemented} {t("implemented")}</span></div>
                      <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-yellow-500" /><span>{analysis.in_progress} {t("in_progress")}</span></div>
                      <div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-500" /><span>{analysis.not_started} {t("not_started")}</span></div>
                      <div className="flex items-center gap-2"><span className="w-4 h-4 rounded-full bg-muted flex-shrink-0" /><span>{analysis.not_applicable} {t("not_applicable")}</span></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">{t("gap.recommendations")}</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {(analysis.recommendations || []).map((rec, i) => (
                      <div key={i} className={`p-3 rounded-lg text-sm ${
                        rec.priority === 'critical' ? 'bg-red-500/10 border-l-2 border-red-500' :
                        rec.priority === 'high' ? 'bg-orange-500/10 border-l-2 border-orange-500' :
                        rec.priority === 'medium' ? 'bg-yellow-500/10 border-l-2 border-yellow-500' :
                        'bg-green-500/10 border-l-2 border-green-500'
                      }`}>{rec.text}</div>
                    ))}
                  </CardContent>
                </Card>
              </div>
              <div className="lg:col-span-2 space-y-4">
                <div className="flex flex-col md:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder={t("search")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="gap-search" />
                  </div>
                  <Select value={filterPriority} onValueChange={setFilterPriority}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("all")}</SelectItem>
                      <SelectItem value="high">{t("high")}</SelectItem>
                      <SelectItem value="medium">{t("medium")}</SelectItem>
                      <SelectItem value="low">{t("low")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("all")}</SelectItem>
                      <SelectItem value="not_started">{t("not_started")}</SelectItem>
                      <SelectItem value="in_progress">{t("in_progress")}</SelectItem>
                      <SelectItem value="implemented">{t("implemented")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-sm text-muted-foreground">{filteredGaps.length} {t("gap.gaps_found")}</p>
                <ScrollArea className="h-[calc(100vh-32rem)]">
                  <div className="space-y-2 pr-4">
                    {filteredGaps.map(gap => (
                      <Card key={gap.control_number}
                        className={`border-border ${expandedGap === gap.control_number ? 'border-primary/50' : ''}`}>
                        <CardContent className="p-3">
                          <div
                            className="flex items-center justify-between gap-2 cursor-pointer"
                            onClick={() => setExpandedGap(expandedGap === gap.control_number ? null : gap.control_number)}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Badge variant="outline" className="font-mono text-xs">{gap.control_number}</Badge>
                              <Badge variant="outline" className={
                                gap.priority === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30' :
                                gap.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30' :
                                'bg-green-100 text-green-700 dark:bg-green-900/30'
                              }>{t(gap.priority)}</Badge>
                              <Badge variant="outline" className={
                                gap.status === 'implemented' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' :
                                gap.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' :
                                gap.status === 'not_applicable' ? 'bg-slate-100 text-slate-700 dark:bg-slate-800' :
                                'bg-rose-100 text-rose-700 dark:bg-rose-900/30'
                              }>{gap.status?.replace('_', ' ')}</Badge>
                              {gap.owner && <Badge variant="secondary" className="text-[10px]">{gap.owner}</Badge>}
                              <span className="text-sm truncate">{gap.title}</span>
                            </div>
                            {!gap.has_evidence && <FileWarning className="w-4 h-4 text-orange-400" />}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingGap({
                                  control_number: gap.control_number,
                                  title: gap.title,
                                  description: gap.description,
                                  status: gap.status || "not_started",
                                  priority: gap.priority || "medium",
                                  notes: gap.notes || "",
                                  remediation_plan: gap.remediation_plan || "",
                                  owner: gap.owner || "",
                                  due_date: gap.due_date || "",
                                  evidence_refs: gap.evidence_refs || [],
                                  document_refs: gap.document_refs || [],
                                });
                              }}
                            >
                              {t("edit")}
                            </Button>
                            {expandedGap === gap.control_number ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </div>
                          {expandedGap === gap.control_number && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 pt-3 border-t border-border space-y-2">
                              <p className="text-sm text-muted-foreground">{gap.description}</p>
                              <div className="flex flex-wrap gap-1">
                                {gap.gap_details?.map((d, i) => <Badge key={i} variant="destructive" className="text-xs">{d}</Badge>)}
                              </div>
                              {gap.notes && (
                                <div className="mt-2">
                                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("gap.notes_comments")}</p>
                                  <p className="text-sm whitespace-pre-wrap">{gap.notes}</p>
                                </div>
                              )}
                              {gap.remediation_plan && (
                                <div className="mt-2">
                                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("gap.remediation_plan")}</p>
                                  <p className="text-sm whitespace-pre-wrap">{gap.remediation_plan}</p>
                                </div>
                              )}
                              <div className="flex gap-3 text-xs text-muted-foreground">
                                {gap.due_date && <span>{t("gap.due_label")}: {gap.due_date}</span>}
                                {(gap.evidence_refs?.length > 0) && <span>{gap.evidence_refs.length} {t("gap.evidences")}</span>}
                                {(gap.document_refs?.length > 0) && <span>{gap.document_refs.length} {t("gap.documents")}</span>}
                              </div>
                            </motion.div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Gap Finding Dialog */}
      <Dialog open={!!editingGap} onOpenChange={(o) => !o && setEditingGap(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("gap.edit_finding")} · {editingGap?.control_number}</DialogTitle>
            <DialogDescription className="line-clamp-2">{editingGap?.title}</DialogDescription>
          </DialogHeader>
          {editingGap && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">{editingGap.description}</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("status")}</Label>
                  <Select
                    value={editingGap.status}
                    onValueChange={(v) => setEditingGap({ ...editingGap, status: v })}
                  >
                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">{t("not_started")}</SelectItem>
                      <SelectItem value="in_progress">{t("in_progress")}</SelectItem>
                      <SelectItem value="implemented">{t("implemented")}</SelectItem>
                      <SelectItem value="not_applicable">{t("not_applicable")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("priority")}</Label>
                  <Select
                    value={editingGap.priority}
                    onValueChange={(v) => setEditingGap({ ...editingGap, priority: v })}
                  >
                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">{t("critical")}</SelectItem>
                      <SelectItem value="high">{t("high")}</SelectItem>
                      <SelectItem value="medium">{t("medium")}</SelectItem>
                      <SelectItem value="low">{t("low")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("owner")}</Label>
                  <Input
                    className="h-9 mt-1"
                    placeholder={t("gap.owner_ph")}
                    value={editingGap.owner}
                    onChange={(e) => setEditingGap({ ...editingGap, owner: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">{t("gap.target_date")}</Label>
                  <Input
                    type="date"
                    className="h-9 mt-1"
                    value={editingGap.due_date}
                    onChange={(e) => setEditingGap({ ...editingGap, due_date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">{t("gap.notes_comments")}</Label>
                <Textarea
                  className="text-sm mt-1 h-20"
                  placeholder={t("gap.notes_ph")}
                  value={editingGap.notes}
                  onChange={(e) => setEditingGap({ ...editingGap, notes: e.target.value })}
                />
              </div>

              <div>
                <Label className="text-xs">{t("gap.remediation_plan")}</Label>
                <Textarea
                  className="text-sm mt-1 h-24"
                  placeholder={t("gap.remediation_ph")}
                  value={editingGap.remediation_plan}
                  onChange={(e) => setEditingGap({ ...editingGap, remediation_plan: e.target.value })}
                />
              </div>

              <div>
                <Label className="text-xs mb-1 block">{t("gap.linked_evidence")}</Label>
                <div className="border border-border rounded-md max-h-32 overflow-y-auto">
                  {evidenceList.length === 0 ? (
                    <p className="p-3 text-xs text-muted-foreground">{t("gap.no_evidence_repo")}</p>
                  ) : (
                    evidenceList.map((ev) => {
                      const id = ev.evidence_id || ev.id;
                      const checked = (editingGap.evidence_refs || []).includes(id);
                      return (
                        <label key={id} className="flex items-center gap-2 p-2 text-sm hover:bg-muted/40 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...(editingGap.evidence_refs || []), id]
                                : (editingGap.evidence_refs || []).filter((x) => x !== id);
                              setEditingGap({ ...editingGap, evidence_refs: next });
                            }}
                          />
                          <span className="truncate flex-1">{ev.title || ev.name || id}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs mb-1 block">{t("gap.linked_documents")}</Label>
                <div className="border border-border rounded-md max-h-32 overflow-y-auto">
                  {documentList.length === 0 ? (
                    <p className="p-3 text-xs text-muted-foreground">{t("gap.no_docs_published")}</p>
                  ) : (
                    documentList.map((doc) => {
                      const id = doc.document_id || doc.id;
                      const checked = (editingGap.document_refs || []).includes(id);
                      return (
                        <label key={id} className="flex items-center gap-2 p-2 text-sm hover:bg-muted/40 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...(editingGap.document_refs || []), id]
                                : (editingGap.document_refs || []).filter((x) => x !== id);
                              setEditingGap({ ...editingGap, document_refs: next });
                            }}
                          />
                          <span className="truncate flex-1">{doc.title || id}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={resetFinding} disabled={savingGap}>{t("gap.revert")}</Button>
            <Button variant="outline" onClick={() => setEditingGap(null)} disabled={savingGap}>{t("cancel")}</Button>
            <Button onClick={saveFinding} disabled={savingGap}>
              {savingGap ? t("gap.saving") : t("gap.save_finding")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("gap.new_project")}</DialogTitle>
            <DialogDescription>{t("gap.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t("gap.select_client")} *</Label>
              {managedOrgs.length === 0 ? (
                <p className="text-sm text-destructive">{t("gap.client_required")}</p>
              ) : (
                <Select value={form.client_org_id} onValueChange={(v) => setForm(f => ({ ...f, client_org_id: v }))}>
                  <SelectTrigger data-testid="client-org-select"><SelectValue placeholder={t("gap.select_client")} /></SelectTrigger>
                  <SelectContent>
                    {managedOrgs.map(o => (
                      <SelectItem key={o.organization_id} value={o.organization_id}>
                        {o.name} {o.is_parent && "(Tu org)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("gap.project_name")} *</Label>
                <Input value={form.project_name} onChange={(e) => setForm(f => ({ ...f, project_name: e.target.value }))}
                  placeholder="GAP ISO 27001 Q2 2026" data-testid="project-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("gap.framework_select")} *</Label>
                <Select value={form.framework} onValueChange={(v) => setForm(f => ({ ...f, framework: v }))}>
                  <SelectTrigger data-testid="framework-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fwEntries.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("gap.contact_name")}</Label>
                <Input value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))}
                  placeholder="Juan Perez" data-testid="contact-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t("gap.contact_email")}</Label>
                <Input type="email" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))}
                  placeholder="juan@cliente.com" data-testid="contact-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("gap.responsible")}</Label>
              <Input value={form.responsible} onChange={(e) => setForm(f => ({ ...f, responsible: e.target.value }))}
                placeholder="Ana Consultor" data-testid="responsible-input" />
            </div>
            <div className="space-y-2">
              <Label>{t("gap.notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2} data-testid="notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} disabled={creating}>{t("cancel")}</Button>
            <Button onClick={submitCreate} disabled={creating || managedOrgs.length === 0} data-testid="submit-gap-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("generating")}</> : <><Play className="w-4 h-4 mr-2" />{t("gap.run_assessment")}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ Project Detail View ============
const ProjectDetailView = ({ project, onBack, token, onRerun, running, t, lang }) => {
  const confirm = useConfirm();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [evidenceList, setEvidenceList] = useState([]);
  const [documentList, setDocumentList] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const snap = project.result_snapshot;

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch the live framework gap analysis so user edits are merged in.
      const res = await axios.get(`${API}/gap-analysis/${project.framework}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetail(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [project.framework, token]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  useEffect(() => {
    (async () => {
      try {
        const [ev, dc] = await Promise.all([
          axios.get(`${API}/evidence`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setEvidenceList(ev.data || []);
        setDocumentList(dc.data || []);
      } catch { /* non-fatal */ }
    })();
  }, [token]);

  const saveFinding = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        status: editing.status,
        priority: editing.priority,
        notes: editing.notes || null,
        remediation_plan: editing.remediation_plan || null,
        owner: editing.owner || null,
        due_date: editing.due_date || null,
        evidence_refs: editing.evidence_refs || [],
        document_refs: editing.document_refs || [],
      };
      await axios.put(
        `${API}/gap-analysis/${project.framework}/finding/${encodeURIComponent(editing.control_number)}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("gap.finding_saved"));
      setEditing(null);
      await fetchDetail();
    } catch (err) {
      toast.error(extractError(err, t("gap.finding_save_failed")));
    } finally {
      setSaving(false);
    }
  };

  const resetFinding = async () => {
    if (!editing) return;
    if (!(await confirm({ title: t("gap.discard_title"), description: t("gap.discard_confirm"), destructive: true }))) return;
    setSaving(true);
    try {
      await axios.delete(
        `${API}/gap-analysis/${project.framework}/finding/${encodeURIComponent(editing.control_number)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t("gap.finding_reverted"));
      setEditing(null);
      await fetchDetail();
    } catch {
      toast.error(t("gap.revert_failed"));
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (g) => setEditing({
    control_number: g.control_number,
    title: g.title,
    description: g.description,
    status: g.status || "not_started",
    priority: g.priority || "medium",
    notes: g.notes || "",
    remediation_plan: g.remediation_plan || "",
    owner: g.owner || "",
    due_date: g.due_date || "",
    evidence_refs: g.evidence_refs || [],
    document_refs: g.document_refs || [],
  });

  const getScoreColor = (pct) => pct >= 80 ? "text-green-500" : pct >= 60 ? "text-yellow-500" : pct >= 30 ? "text-orange-500" : "text-red-500";

  return (
    <div className="space-y-4" data-testid="project-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-2" />{t("back")}</Button>

      <Card className="border-border">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-2xl">{project.project_name}</CardTitle>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><Building2 className="w-3 h-3" />{project.client_name} · {fwNames[project.framework]}</p>
            </div>
            <Button onClick={() => onRerun(project.engagement_id)} disabled={running} data-testid="detail-rerun">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
              {t("gap.run_again")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {project.contact_name && <div><p className="text-xs text-muted-foreground">{t("gap.contact_name")}</p><p className="text-sm font-medium">{project.contact_name}</p></div>}
          {project.contact_email && <div><p className="text-xs text-muted-foreground">{t("gap.contact_email")}</p><p className="text-sm font-medium flex items-center gap-1"><Mail className="w-3 h-3" />{project.contact_email}</p></div>}
          {project.responsible && <div><p className="text-xs text-muted-foreground">{t("gap.responsible")}</p><p className="text-sm font-medium">{project.responsible}</p></div>}
          <div><p className="text-xs text-muted-foreground">{t("status")}</p><p className="text-sm font-medium">{t(`eng.status.${project.status}`)}</p></div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : detail ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-lg">{t("gap.coverage")}</CardTitle></CardHeader>
            <CardContent className="text-center">
              <span className={`text-5xl font-heading font-bold ${getScoreColor(detail.coverage_percentage)}`}>{detail.coverage_percentage}%</span>
              <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-left">
                <div className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" />{detail.implemented} {t("implemented")}</div>
                <div className="flex items-center gap-1"><Clock className="w-3 h-3 text-yellow-500" />{detail.in_progress} {t("in_progress")}</div>
                <div className="flex items-center gap-1"><XCircle className="w-3 h-3 text-red-500" />{detail.not_started} {t("not_started")}</div>
                <div className="flex items-center gap-1">{detail.not_applicable} {t("not_applicable")}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border lg:col-span-2">
            <CardHeader className="pb-2"><CardTitle className="text-sm">{t("gap.recommendations")}</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-96 overflow-auto">
              {(detail.recommendations || []).map((rec, i) => (
                <div key={i} className={`p-3 rounded-lg text-sm ${
                  rec.priority === 'critical' ? 'bg-red-500/10 border-l-2 border-red-500' :
                  rec.priority === 'high' ? 'bg-orange-500/10 border-l-2 border-orange-500' :
                  'bg-yellow-500/10 border-l-2 border-yellow-500'
                }`}>{rec.text}</div>
              ))}
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Editable findings list */}
      {detail && (
        <Card className="border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t("gap.findings")} · {detail.gap_count}</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[480px]">
              <div className="space-y-2 pr-3">
                {(detail.gaps || []).map((g) => (
                  <Card key={g.control_number} className="border-border">
                    <CardContent className="p-3">
                      <div
                        className="flex items-center justify-between gap-2 cursor-pointer"
                        onClick={() => setExpandedRow(expandedRow === g.control_number ? null : g.control_number)}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Badge variant="outline" className="font-mono text-xs">{g.control_number}</Badge>
                          <Badge variant="outline" className={
                            g.priority === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30' :
                            g.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30' :
                            g.priority === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-900/30' :
                            'bg-green-100 text-green-700 dark:bg-green-900/30'
                          }>{g.priority}</Badge>
                          <Badge variant="outline" className={
                            g.status === 'implemented' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' :
                            g.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' :
                            g.status === 'not_applicable' ? 'bg-slate-100 text-slate-700 dark:bg-slate-800' :
                            'bg-rose-100 text-rose-700 dark:bg-rose-900/30'
                          }>{(g.status || 'not_started').replace('_', ' ')}</Badge>
                          {g.owner && <Badge variant="secondary" className="text-[10px]">{g.owner}</Badge>}
                          <span className="text-sm truncate">{g.title}</span>
                        </div>
                        {!g.has_evidence && <FileWarning className="w-4 h-4 text-orange-400" />}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); openEditor(g); }}
                        >
                          Editar
                        </Button>
                        {expandedRow === g.control_number ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                      {expandedRow === g.control_number && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          <p className="text-sm text-muted-foreground">{g.description}</p>
                          {g.gap_details?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {g.gap_details.map((d, i) => <Badge key={i} variant="destructive" className="text-xs">{d}</Badge>)}
                            </div>
                          )}
                          {g.notes && (
                            <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("gap.notes_comments")}</p><p className="text-sm whitespace-pre-wrap">{g.notes}</p></div>
                          )}
                          {g.remediation_plan && (
                            <div><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("gap.remediation_plan")}</p><p className="text-sm whitespace-pre-wrap">{g.remediation_plan}</p></div>
                          )}
                          <div className="flex gap-3 text-xs text-muted-foreground">
                            {g.due_date && <span>{t("gap.due_label")}: {g.due_date}</span>}
                            {(g.evidence_refs?.length > 0) && <span>{g.evidence_refs.length} {t("gap.evidences")}</span>}
                            {(g.document_refs?.length > 0) && <span>{g.document_refs.length} {t("gap.documents")}</span>}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Edit Finding Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("gap.edit_finding")} · {editing?.control_number}</DialogTitle>
            <DialogDescription className="line-clamp-2">{editing?.title}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">{editing.description}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("status")}</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">{t("not_started")}</SelectItem>
                      <SelectItem value="in_progress">{t("in_progress")}</SelectItem>
                      <SelectItem value="implemented">{t("implemented")}</SelectItem>
                      <SelectItem value="not_applicable">{t("not_applicable")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("priority")}</Label>
                  <Select value={editing.priority} onValueChange={(v) => setEditing({ ...editing, priority: v })}>
                    <SelectTrigger className="h-9 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">{t("critical")}</SelectItem>
                      <SelectItem value="high">{t("high")}</SelectItem>
                      <SelectItem value="medium">{t("medium")}</SelectItem>
                      <SelectItem value="low">{t("low")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("owner")}</Label>
                  <Input className="h-9 mt-1" placeholder={t("gap.owner_ph")}
                    value={editing.owner} onChange={(e) => setEditing({ ...editing, owner: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">{t("gap.target_date")}</Label>
                  <Input type="date" className="h-9 mt-1"
                    value={editing.due_date} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">{t("gap.notes_comments")}</Label>
                <Textarea className="text-sm mt-1 h-20" placeholder={t("gap.notes_ph")}
                  value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">{t("gap.remediation_plan")}</Label>
                <Textarea className="text-sm mt-1 h-24" placeholder={t("gap.remediation_ph")}
                  value={editing.remediation_plan} onChange={(e) => setEditing({ ...editing, remediation_plan: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("gap.linked_evidence")}</Label>
                <div className="border border-border rounded-md max-h-32 overflow-y-auto">
                  {evidenceList.length === 0 ? (
                    <p className="p-3 text-xs text-muted-foreground">{t("gap.no_evidence_repo")}</p>
                  ) : evidenceList.map((ev) => {
                    const id = ev.evidence_id || ev.id;
                    const checked = (editing.evidence_refs || []).includes(id);
                    return (
                      <label key={id} className="flex items-center gap-2 p-2 text-sm hover:bg-muted/40 cursor-pointer">
                        <input type="checkbox" checked={checked}
                          onChange={(e) => setEditing({
                            ...editing,
                            evidence_refs: e.target.checked
                              ? [...(editing.evidence_refs || []), id]
                              : (editing.evidence_refs || []).filter((x) => x !== id),
                          })} />
                        <span className="truncate flex-1">{ev.title || ev.name || id}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("gap.linked_documents")}</Label>
                <div className="border border-border rounded-md max-h-32 overflow-y-auto">
                  {documentList.length === 0 ? (
                    <p className="p-3 text-xs text-muted-foreground">{t("gap.no_docs_published")}</p>
                  ) : documentList.map((doc) => {
                    const id = doc.document_id || doc.id;
                    const checked = (editing.document_refs || []).includes(id);
                    return (
                      <label key={id} className="flex items-center gap-2 p-2 text-sm hover:bg-muted/40 cursor-pointer">
                        <input type="checkbox" checked={checked}
                          onChange={(e) => setEditing({
                            ...editing,
                            document_refs: e.target.checked
                              ? [...(editing.document_refs || []), id]
                              : (editing.document_refs || []).filter((x) => x !== id),
                          })} />
                        <span className="truncate flex-1">{doc.title || id}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={resetFinding} disabled={saving}>{t("gap.revert")}</Button>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>{t("cancel")}</Button>
            <Button onClick={saveFinding} disabled={saving}>
              {saving ? t("gap.saving") : t("gap.save_finding")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GapAnalysis;
